import { assertType, type Guard } from '@calloneer/contracts';

export interface ApiRequest<T> {
  readonly path: string;
  readonly guard: Guard<T>;
  readonly method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  readonly body?: unknown;
}
export const isApiRequest = (value: unknown): value is ApiRequest<unknown> =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as Partial<ApiRequest<unknown>>).path === 'string' &&
  typeof (value as Partial<ApiRequest<unknown>>).guard === 'function';

export const apiRequest = async <T>(input: ApiRequest<T>): Promise<T> => {
  if (!isApiRequest(input)) throw new TypeError('Invalid API request');
  const init: RequestInit = {
    method: input.method ?? 'GET',
    credentials: 'include',
    ...(input.body === undefined
      ? {}
      : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input.body) }),
  };
  const response = await fetch(input.path, init);
  const payload: unknown = response.status === 204 ? null : await response.json();
  if (!response.ok) {
    const message =
      typeof payload === 'object' && payload !== null && 'message' in payload
        ? String(payload.message)
        : 'Request failed';
    throw new Error(message);
  }
  return assertType({ name: `response from ${input.path}`, value: payload, guard: input.guard });
};

export const isNull = (value: unknown): value is null => value === null;
export const isUnknownRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;
export const isUnknownArray = (value: unknown): value is readonly unknown[] => Array.isArray(value);
